/**
 * Chart documentation shown behind the info icon on the Sales dashboard.
 *
 * Keys match the chart link property names in `MopaSalesComponent`; `comparison`
 * is shared by the two store-comparison charts and was moved verbatim out of
 * `data-loaders/sales-comparison.ts`.
 */
export const MopaSalesChartDocs = {
    arpu: /* HTML */ `<p>
            Running (cumulative) Average Revenue Per User for a first-open
            cohort, by day-number since install.
        </p>
        <p>Daily ARPU for a given day-number:</p>
        <pre>
  revenue the cohort earned on that day-number   (numerator)
  ────────────────────────────────────────────
  cohort size matured to that day-number         (denominator)</pre
        >
        <p>
            The <strong>denominator</strong> counts only players whose tracked
            lifetime reaches at least that many days - players who installed too
            recently are not in it yet, so late day-numbers aren't diluted. The
            line plots the running sum of these daily ARPU values, so it only
            ever climbs and the last point is the cohort's lifetime-to-date
            ARPU.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size).
        </p>`,

    r_rev_perc_total: /* HTML */ `<p>
            Cumulative share of a first-open cohort's total revenue that has
            been collected by a given day-number since install - a monetization
            pace curve.
        </p>
        <pre>
  cohort revenue summed from day 0 to that day-number   (numerator)
  ───────────────────────────────────────────────────
  cohort's total revenue across the whole window        (denominator)</pre
        >
        <p>
            The <strong>denominator</strong> is a fixed per-cohort total, so it
            doesn't change along the x-axis; the <strong>numerator</strong>
            grows toward it, so the series always ends at 100%. A steeper early
            slope means revenue is captured sooner in the player's life.
        </p>
        <p>Source: <code>bi_viz.dashboard_sales</code>.</p>`,

    revenue: /* HTML */ `<p>
            Total revenue recorded on a given date (or period under date
            aggregation).
        </p>
        <p>
            <strong>What's counted:</strong> per-day revenue rows from the sales
            table, added up over the date/period. Not a rate - there is no
            denominator. The Net/Gross toggle picks <em>gross</em> (the store
            price of the product - the full amount the player pays) or
            <em>net</em> (exactly what the studio actually received, after the
            store's commission).
        </p>
        <p>Source: <code>bi_viz.dashboard_sales</code>.</p>`,

    new_payers: /* HTML */ `<p>
            Number of players who made their first-ever purchase on a given date
            (or period).
        </p>
        <p>
            <strong>What's counted:</strong> first-transaction events, summed
            over the date/period. Since a player has exactly one first-ever
            purchase, this equals the count of distinct brand-new payers - a
            player who buys again later is not counted again.
        </p>
        <p>Source: <code>bi_viz.dashboard_sales</code>.</p>`,

    transactions: /* HTML */ `<p>
            Total number of purchase transactions recorded on a given date (or
            period).
        </p>
        <p>
            <strong>What's counted:</strong> every purchase transaction, summed
            over the date/period - <em>not</em> distinct payers. One player
            making three purchases in a day adds 3, whereas "New payers" would
            add at most 1.
        </p>
        <p>Source: <code>bi_viz.dashboard_sales</code>.</p>`,

    wavg_arpdau: /* HTML */ `<p>
            Average revenue per daily active user - how much an average active
            player is worth on an average day.
        </p>
        <pre>
  total revenue over the date / period          (numerator)
  ────────────────────────────────────────
  sum of each day's active users (DAU)          (denominator)</pre
        >
        <p>
            This is a weighted daily average: add up daily revenue, add up daily
            DAU, then divide - so a high-traffic day counts more. Under date
            aggregation (week, month, …) it stays weighted, <em>not</em> a plain
            average of the individual daily ARPDAU values.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (DAU).
        </p>`,

    wavg_cr: /* HTML */ `<p>
            Share of active users who made at least one purchase - the
            paying-conversion of the active base.
        </p>
        <pre>
  sum of each day's payers (distinct buyers that day)   (numerator)
  ───────────────────────────────────────────────────
  sum of each day's active users (DAU)                  (denominator)</pre
        >
        <p>
            This is a weighted daily average (add up daily payers, add up daily
            DAU, then divide) - <em>not</em> a plain average of the daily rates,
            so busy days weigh more. A player who pays on two days counts on
            each of those days.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (payers),
            <code>bi_viz.dashboard_users_activity</code> (DAU).
        </p>`,

    invalid_transactions: /* HTML */ `<p>
            Count of purchase transactions flagged invalid in the analytics
            event stream (e.g. unverified, duplicate or repeated events) on a
            given date.
        </p>
        <p>
            <strong>What's counted:</strong> the invalid-flagged transaction
            count summed over the date/period. these are excluded from the valid
            revenue/transaction figures and shown here on their own so a spike
            (a data-quality or fraud signal) is visible rather than silently
            dropped.
        </p>
        <p>
            Source: analytics events behind
            <code>bi_viz.dashboard_sales_comparison</code>.
        </p>`,

    comparison: `<h2>Definition</h2>
                <p>Shows the <strong>absolute difference</strong> between sales values derived from analytics events (AM BI) and official store reports — covering transaction counts as well as net and gross revenue (PLN &amp; USD).</p>
                <h2>How it works</h2>
                <ul>
                    <li>Data is cut off at the latest date for which analytics events exist.</li>
                    <li>Differences are only calculated for dates where <strong>both</strong> analytics data and store reports are available. Dates with missing reports are shown as gaps (NULL) instead of misleading zeros.</li>
                    <li>Invalid transactions are reported separately so you can assess data-quality impact.</li>
                </ul>
                <h2>Tips</h2>
                <ul>
                    <li>Use the <strong>app store split</strong> to narrow the view to stores that already have reports — this lets you see the most up-to-date comparison even when some stores haven't reported yet.</li>
                </ul>
                `,
} as const;
